import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	Signal,
	signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { MarkdownModule } from 'ngx-markdown';
import { map, of, switchMap } from 'rxjs';

import { CarouselComponent } from '@valk-nx/components/ui-carousel/src/lib/carousel';
import { type CarouselItem } from '@valk-nx/components/ui-carousel/src/lib/carousel.interface';
import { type Icon } from '@valk-nx/components/ui-icon-list/src/lib/icon-list.interface';
import { ListGridComponent } from '@valk-nx/components/ui-list-grid/src/lib/list-grid';
import { MapComponent } from '@valk-nx/components/ui-map/src/lib/map';
import { ReadMoreComponent } from '@valk-nx/components/ui-read-more/src/lib/read-more';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import {
	fallbackLanguage,
	HOTEL_SLUG,
	LANGUAGE,
	Language,
	negativeHeader$,
	PRICING_TOOLTIP_OCCUPANCY,
} from '@valk-nx/core/lib/core';
import {
	DATE_FORMAT,
	TIME_INPUT_FORMAT,
	TIME_OUTPUT_FORMAT,
} from '@valk-nx/core/lib/date/date-format';
import { type PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import type { PriceInterface } from '@valk-nx/core/lib/interfaces/price.interface';
import { SectionBgClass } from '@valk-nx/core/lib/pipes/section-class/section-class.pipe';
import { type PricingTooltip } from '@valk-nx/helpers/lib/pricing-tooltip/pricing-tooltip.interface';
import { AccordionComponent as SbAccordionComponent } from '@valk-nx/storyblok/components/accordion/src/lib/accordion';
import { type AccordionItem } from '@valk-nx/storyblok/components/accordion/src/lib/accordion.interface';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { buildEventIconList } from '@valk-nx/storyblok-helpers/src/lib/general/icon-list.helper';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { type EventDetailData } from '@valk-nx/storyblok-types/src/lib/types/content.interface';

import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';

interface PackageBatchCard {
	cardUrl: string;
	content: string;
	ctaTarget: '_self' | '_blank';
	ctaText: string;
	ctaUrl: string;
	images: PhotoInterface[];
	price: PriceInterface;
	pricingToolTip?: PricingTooltip;
	title: string;
}

@Component({
	selector: 'sb-event-detail',
	templateUrl: './event-detail.component.html',
	imports: [
		CarouselComponent,
		CommonModule,
		ListGridComponent,
		MarkdownModule,
		MapComponent,
		PersuadeCardComponent,
		ReadMoreComponent,
		RouterModule,
		SectionBgClass,
		TranslatePipe,
		SbAccordionComponent,
	],
})
export class EventDetailComponent extends StoryblokRootDirective {
	private readonly contentService = inject(ContentService);
	private readonly sbService = inject(StoryblokService);
	private readonly metadataService = inject(MetadataService);
	private readonly translate = inject(TranslateService);
	private readonly rootLanguage = inject<Language>(LANGUAGE);

	readonly currentLanguage = signal<string>(fallbackLanguage);
	readonly language: string = this.rootLanguage;
	readonly hotelSlug = inject(HOTEL_SLUG);

	dateFormat = DATE_FORMAT;
	timeInputFormat = TIME_INPUT_FORMAT;
	timeOutputFormat = TIME_OUTPUT_FORMAT;

	eventData: Signal<EventDetailData | null> = toSignal(
		this.sbService.translatedSlugs$.pipe(
			takeUntilDestroyed(),
			map((translatedSlugs) => {
				return (
					translatedSlugs.find((x) => x.selected) ??
					translatedSlugs[0]
				);
			}),
			switchMap((slug) => {
				const eventSlug = slug.path.split('/').pop();

				if (!eventSlug) {
					return of(null);
				}

				return this.contentService.getEvent(
					eventSlug,
					this.hotelSlug,
					slug.lang.toLowerCase(),
				);
			}),
			map((response) => {
				if (!response) {
					return null;
				}

				this.metadataService.updateMetaDataBatch(
					response.data,
					this.robotsNoFollow,
					this.robotsNoIndex,
				);

				return response.data;
			}),
		),
		{ initialValue: null },
	);

	eventDetailsIconList: Signal<Icon[]> = computed(() => {
		const data = this.eventData();

		if (!data) {
			return [];
		}

		return buildEventIconList(data, {
			dateFormat: this.dateFormat,
			timeInputFormat: this.timeInputFormat,
			timeOutputFormat: this.timeOutputFormat,
			translateFn: (key) => this.translate.instant(key),
		});
	});

	eventDates = computed(() => {
		const data = this.eventData();
		if (!data?.schedule?.dates) {
			return [];
		}

		return data.schedule.dates.map((date: string) =>
			dayjs(date).format('dd DD MMM YYYY'),
		);
	});

	eventImages: Signal<CarouselItem[]> = computed(() => {
		const data = this.eventData();

		if (!data || data.images.length === 0) {
			return [];
		}

		return data.images.map((image: PhotoInterface) => {
			return {
				image: {
					src: image.src,
					alt: image.alt ?? '',
				},
				heading: '',
				subHeading: '',
				link: {
					content: '',
					url: '',
				},
			};
		});
	});

	eventInformationBullets = computed(() => {
		const bulletContent = this.eventData()?.bulletContent;

		if (!bulletContent) {
			return [];
		}
		// Check if it's markdown starting with "- " and split into array
		// If not just take the entire string and return as single item array
		const isMarkdownBullets = bulletContent.startsWith('- ');
		if (!isMarkdownBullets) {
			return [bulletContent];
		}
		const bullets = bulletContent
			.split('\n')
			.map((bullet) => bullet.replace(/^- /, '').trim())
			.filter((bullet) => bullet.length > 0);

		return bullets;
	});

	eventFaqs: Signal<AccordionItem[]> = computed(() => {
		const data = this.eventData();

		if (!data?.faqs) {
			return [];
		}

		return data.faqs.map((faq) => ({
			title: faq.question,
			content: faq.answer,
		}));
	});

	packageBatchCards: Signal<PackageBatchCard[]> = computed(() => {
		const data = this.eventData();

		if (!data?.packageBatches || data.packageBatches.length === 0) {
			return [];
		}

		return data.packageBatches.map((packageBatch) => ({
			images: packageBatch.images,
			title: packageBatch.name,
			cardUrl: packageBatch.toUrl,
			price: packageBatch.price,
			pricingToolTip: {
				occupancy: PRICING_TOOLTIP_OCCUPANCY,
			},
			content: packageBatch.content.value,
			ctaUrl: packageBatch.toUrl,
			ctaText: this.translate.instant(
				'storyblok.package-event.cta',
			) as string,
			ctaTarget: '_self' as const,
		}));
	});

	constructor() {
		super();

		this.currentLanguage.set(
			this.translate.getCurrentLang() ?? this.translate.getFallbackLang(),
		);

		effect(() => {
			const data = this.eventData();

			if (!data || data.images.length === 0) {
				negativeHeader$.next(false);
			} else {
				negativeHeader$.next(true);
			}
		});
	}
}
